import { AlertCircle, CheckCircle2, CircleDashed, Clock, HelpCircle, RefreshCw, Undo2, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Canonical → display bucket mapping. The wire is one of:
 *   started · reserved · paid · confirmed · cancelled · completed · refunded ·
 *   refund_failed · partially_refunded · partially_completed
 * `started` is filtered out by the orders API; we still handle it defensively.
 */
const DISPLAY_BUCKET = {
	started: 'Pending',
	reserved: 'Pending',
	paid: 'Pending',
	confirmed: 'Confirmed',
	completed: 'Completed',
	partially_completed: 'Completed',
	cancelled: 'Cancelled',
	refunded: 'Cancelled',
	partially_refunded: 'Partially refunded',
	refund_failed: 'Refund failed'
};

const BUCKET_STYLES = {
	Pending: { pill: 'bg-warning-soft text-warning-soft-foreground', Icon: Clock },
	Confirmed: { pill: 'bg-info-soft text-info-soft-foreground', Icon: CircleDashed },
	Completed: { pill: 'bg-success-soft text-success-soft-foreground', Icon: CheckCircle2 },
	Cancelled: { pill: 'bg-muted text-muted-foreground', Icon: XCircle },
	'Partially refunded': { pill: 'bg-warning-soft text-warning-soft-foreground', Icon: Undo2 },
	'Refund failed': { pill: 'bg-destructive/10 text-destructive', Icon: AlertCircle }
};

// Genuinely unknown statuses render as-is in a neutral pill — unknown ≠ action needed.
const UNKNOWN_STYLE = { pill: 'bg-muted text-muted-foreground', Icon: HelpCircle };

export const ORDER_DISPLAY_BUCKETS = ['Pending', 'Confirmed', 'Completed', 'Partially refunded', 'Cancelled'];

/**
 * Expand a list of display buckets to the canonical wire statuses they cover.
 * Useful for applying a checkbox filter back against the orders list.
 */
export function expandBuckets(buckets) {
	const expanded = new Set();
	for (const bucket of buckets) {
		for (const [wire, display] of Object.entries(DISPLAY_BUCKET)) {
			if (display === bucket) expanded.add(wire);
		}
	}
	return expanded;
}

export function bucketFor(status) {
	if (DISPLAY_BUCKET[status]) return DISPLAY_BUCKET[status];
	// Unknown statuses render the raw value harmlessly instead of pretending
	// to be Pending (which would pin them into "Action needed").
	const raw = String(status || 'unknown').replaceAll('_', ' ');
	return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function StatusPill({ status, refundPending = false }) {
	// Lock the pill to a fixed `h-6` (24px) so icon-specific glyph metrics —
	// CircleDashed's segments render slightly taller than CheckCircle2/XCircle
	// at the same `h-3.5` — can't bump the box height between status types.
	// `leading-none` keeps the text from contributing its own line-box height.
	const base = 'inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold leading-none';

	if (refundPending) {
		return (
			<span className={cn(base, 'bg-warning-soft text-warning-soft-foreground')}>
				<RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin" />
				Refund processing
			</span>
		);
	}

	const bucket = bucketFor(status);
	const cfg = BUCKET_STYLES[bucket] || UNKNOWN_STYLE;
	const { Icon } = cfg;
	return (
		<span className={cn(base, cfg.pill)}>
			<Icon className="h-3.5 w-3.5 shrink-0" />
			{bucket}
		</span>
	);
}
